import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { PawPrint, Briefcase, Download, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { CAREER_DETAIL } from "@/constants/testIds";

const paidKey = (careerId) => `career-paid-session-${careerId}`;

export default function CareerDetail() {
  const { id } = useParams();
  const [career, setCareer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [paidSessionId, setPaidSessionId] = useState(() => localStorage.getItem(paidKey(id)) || "");
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    api
      .get(`/careers/${id}`)
      .then((r) => setCareer(r.data))
      .catch(() => setCareer(null))
      .finally(() => setLoading(false));
  }, [id]);

  const pollStatus = useCallback(
    async (sessionId, attempt = 0) => {
      if (attempt > 10) {
        setVerifying(false);
        toast.error("Payment verification timed out. If you were charged, contact us on WhatsApp.");
        return;
      }
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setPaidSessionId(sessionId);
          localStorage.setItem(paidKey(id), sessionId);
          setVerifying(false);
          toast.success("Payment successful! Your download is unlocked.");
          return;
        }
        setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
      } catch {
        setVerifying(false);
      }
    },
    [id]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (sessionId && sessionId !== paidSessionId) {
      setVerifying(true);
      pollStatus(sessionId);
    }
  }, [pollStatus, paidSessionId]);

  const startCheckout = async () => {
    setCheckingOut(true);
    try {
      const res = await api.post(`/careers/${id}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Unable to start payment. Please try again.");
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D97736] animate-spin" />
      </div>
    );
  }

  if (!career) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex flex-col items-center justify-center gap-4">
        <p className="text-[#0F2520]/70">This career listing could not be found.</p>
        <Link to="/" className="text-[#D97736] font-semibold">Back to home</Link>
      </div>
    );
  }

  return (
    <div data-testid={CAREER_DETAIL.page} className="min-h-screen bg-[#F9F8F6]">
      <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
        <Link to="/" className="flex items-center gap-2 mb-10 text-[#0F2520]/60 hover:text-[#D97736] transition-colors">
          <PawPrint className="w-5 h-5" /> Raijada Dog Kennel
        </Link>

        <div className="grid md:grid-cols-5 gap-10">
          <div className="md:col-span-2">
            <Briefcase className="w-9 h-9 text-[#D97736] mb-4" />
            <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0F2520] mb-6">{career.title}</h1>

            <div className="space-y-5 text-sm">
              <div>
                <p className="uppercase tracking-widest text-xs font-bold text-[#D97736] mb-1">Qualification</p>
                <p className="text-[#0F2520]/80 font-light">{career.qualification}</p>
              </div>
              <div>
                <p className="uppercase tracking-widest text-xs font-bold text-[#D97736] mb-1">Skills</p>
                <p className="text-[#0F2520]/80 font-light">{career.skills}</p>
              </div>
              <div>
                <p className="uppercase tracking-widest text-xs font-bold text-[#D97736] mb-1">Education</p>
                <p className="text-[#0F2520]/80 font-light">{career.education}</p>
              </div>
            </div>

            <div className="mt-10 bg-[#0F2520] rounded-[1.5rem] p-6 text-white">
              <p className="text-white/60 text-sm mb-1">Application Form (with QR)</p>
              <p className="font-display text-2xl font-bold mb-4">₹{career.price}</p>

              {paidSessionId ? (
                <a
                  data-testid={CAREER_DETAIL.downloadBtn}
                  href={`${API}/careers/${id}/download?session_id=${paidSessionId}`}
                  className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#F4D06F] text-[#0F2520] font-semibold hover:bg-white transition-colors"
                >
                  <Download className="w-4 h-4" /> Download Full Application PDF
                </a>
              ) : (
                <Button
                  data-testid={CAREER_DETAIL.payBtn}
                  onClick={startCheckout}
                  disabled={checkingOut || verifying}
                  className="w-full h-12 rounded-full bg-[#D97736] hover:bg-[#F4D06F] hover:text-[#0F2520] text-white font-semibold"
                >
                  {verifying ? "Verifying payment..." : checkingOut ? "Redirecting..." : `Pay ₹${career.price} & Unlock Full PDF`}
                </Button>
              )}
              {verifying && (
                <p data-testid={CAREER_DETAIL.statusMsg} className="flex items-center gap-2 text-xs text-white/60 mt-3">
                  <Loader2 className="w-3 h-3 animate-spin" /> Confirming your payment with Stripe...
                </p>
              )}
              {paidSessionId && (
                <p className="flex items-center gap-2 text-xs text-[#F4D06F] mt-3">
                  <CheckCircle2 className="w-3 h-3" /> Payment confirmed
                </p>
              )}
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="uppercase tracking-widest text-xs font-bold text-[#D97736] mb-3">
              {paidSessionId ? "Full Application Form" : "Free Preview (first half of pages)"}
            </p>
            <div className="rounded-[1.5rem] overflow-hidden border border-[#DCD7CE] bg-white" style={{ height: "80vh" }}>
              <iframe
                data-testid={CAREER_DETAIL.previewFrame}
                title="Career application PDF preview"
                src={paidSessionId ? `${API}/careers/${id}/download?session_id=${paidSessionId}` : `${API}/careers/${id}/preview`}
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
